<template>
  <div>
    <v-layout
      row
      justify-center
    >
      <v-dialog
        v-model="addEditModal"
        persistent
        max-width="640px"
      >
        <v-card v-if="addEditModal">
          <form @submit.prevent="submitForm()">
            <v-card-title class="pb-0">
              <v-flex
                xs11
                sm10
                md10
              >
                <span class="headline">
                  {{ isUpdate ? $t('linkRules.updateLinkRuleTitle') : $t('linkRules.addNewLinkRuleTitle') }}
                </span>
              </v-flex>
              <v-flex
                xs1
                sm2
                md2
              >
                <v-btn
                  flat
                  @click="closeDialog()"
                  class="close"
                >
                  <span aria-hidden="true">
              <i class="material-icons" style="font-size: 24px;vertical-align: middle;">close</i>
            </span></v-btn>
              </v-flex>
            </v-card-title>
            <v-card-text>
              <v-layout wrap>
                <v-flex xs12>
                  <v-text-field
                    v-model="form.name"
                    :label="$t('columns.name')"
                    :hint="$t('linkRules.nameHint')"
                    persistent-hint
                    :error-messages="errors.collect('Name')"
                    v-validate="'required'"
                    data-vv-name="Name"
                  />
                </v-flex>

                <v-flex xs12>
                  <v-textarea
                    v-model="testUrl"
                    :label="$t('linkRules.testUrlLabel')"
                    :hint="$t('linkRules.testUrlHint')"
                    persistent-hint
                    rows="2"
                  />
                </v-flex>

                <v-flex xs12>
                  <v-text-field
                    v-model="form.domain"
                    :label="$t('columns.domain')"
                    :hint="$t('linkRules.domainHint')"
                    persistent-hint
                    :error-messages="errors.collect('Domain')"
                    v-validate="'required'"
                    data-vv-name="Domain"
                  />
                </v-flex>

                <v-flex xs12>
                  <v-checkbox
                    v-model="form.is_short_link"
                    :label="$t('linkRules.shortLinkCheckboxLabel')"
                  />
                </v-flex>

                <v-flex
                  xs12
                  v-if="form.is_short_link"
                >
                  <v-btn
                    small
                    outline
                    :loading="resolving"
                    :disabled="!testUrl"
                    @click="resolveShortLink()"
                  >
                    {{ $t('linkRules.resolveButton') }}
                  </v-btn>
                  <div
                    v-if="resolvedUrl"
                    class="text-caption mt-2"
                  >
                    {{ $t('linkRules.resolvesToLabel') }} <strong>{{ resolvedUrl }}</strong>
                  </div>
                  <div
                    v-if="resolveError"
                    class="text-caption mt-2"
                    style="color: #d32f2f"
                  >
                    {{ resolveError }}
                  </div>
                </v-flex>

                <!-- Live preview: mirrors the app's own detection/extraction logic
                     (rule pattern if set, generic fallback otherwise) against the
                     pasted Test URL, so the admin can confirm before saving. -->
                <v-flex
                  xs12
                  v-if="preview"
                >
                  <v-alert
                    :value="true"
                    :color="preview.isProductPage ? 'success' : 'warning'"
                    outline
                    class="mb-2"
                  >
                    <div>
                      {{ preview.isProductPage ? $t('linkRules.detectedAsProductPage') : $t('linkRules.notDetectedAsProductPage') }}
                      <span class="text-caption">({{ preview.detectionSource }})</span>
                    </div>
                    <div class="mt-1">
                      {{ $t('linkRules.previewExtractedNameLabel') }}
                      <strong>{{ preview.name || '—' }}</strong>
                      <span class="text-caption">({{ preview.nameSource }})</span>
                    </div>
                  </v-alert>
                </v-flex>

                <v-flex xs12>
                  <a @click="advancedOpen = !advancedOpen">
                    {{ advancedOpen ? $t('linkRules.hideAdvancedOptions') : $t('linkRules.advancedOptionsHint') }}
                  </a>
                </v-flex>

                <template v-if="advancedOpen">
                  <v-flex
                    xs12
                    sm6
                    class="px-2"
                  >
                    <v-select
                      :items="pageMatchTypes"
                      v-model="form.product_page_match_type"
                      :label="$t('linkRules.productPageMatchTypeLabel')"
                      clearable
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    sm6
                    class="px-2"
                  >
                    <v-text-field
                      v-model="form.product_page_pattern"
                      :label="$t('linkRules.productPagePatternLabel')"
                      :hint="$t('linkRules.productPagePatternHint')"
                      persistent-hint
                    />
                  </v-flex>

                  <v-flex xs12>
                    <v-text-field
                      v-model="form.exclude_path_prefixes"
                      :label="$t('linkRules.excludePathPrefixesLabel')"
                      :hint="$t('linkRules.excludePathPrefixesHint')"
                      persistent-hint
                    />
                  </v-flex>

                  <v-flex
                    xs12
                    sm6
                    class="px-2"
                  >
                    <v-select
                      :items="nameExtractTypes"
                      v-model="form.name_extract_match_type"
                      :label="$t('linkRules.nameExtractionTypeLabel')"
                      clearable
                    />
                  </v-flex>
                  <v-flex
                    xs12
                    sm6
                    class="px-2"
                  >
                    <v-text-field
                      v-model="form.name_extract_pattern"
                      :label="$t('linkRules.nameExtractionPatternLabel')"
                      :hint="$t('linkRules.nameExtractionPatternHint')"
                      persistent-hint
                    />
                  </v-flex>

                  <v-flex xs12>
                    <v-text-field
                      v-model="form.name_cleanup_regex"
                      :label="$t('linkRules.nameCleanupRegexLabel')"
                      :hint="$t('linkRules.nameCleanupRegexHint')"
                      persistent-hint
                    />
                  </v-flex>
                </template>

                <v-flex
                  xs12
                  sm6
                  class="px-2"
                >
                  <v-text-field
                    v-model.number="form.priority"
                    :label="$t('linkRules.priorityLabel')"
                    type="number"
                    :hint="$t('linkRules.priorityHint')"
                    persistent-hint
                  />
                </v-flex>
                <v-flex
                  xs12
                  sm6
                  class="px-2"
                >
                  <v-checkbox
                    v-model="form.is_active"
                    :label="$t('common.active')"
                  />
                </v-flex>

                <v-flex xs12>
                  <v-textarea
                    v-model="form.notes"
                    :label="$t('linkRules.notesLabel')"
                    rows="2"
                  />
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>

              <v-btn
                style="height:auto"
                @click="closeDialog()"
                outline
                color="normal"
              >{{ $t('common.cancel') }}</v-btn>
              <v-btn
                type="submit"
                :loading="loading"
                style="height:auto" class="submit-btn-spacing"
                outline
                color="success"
              >
                {{ isUpdate ? $t('common.save') : $t('common.add') }}
              </v-btn>
            </v-card-actions>
          </form>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>
<script>
import Axios from 'axios'
import swal from 'sweetalert'

// Mirrors the Flutter app's built-in generic fallback (see
// lib/UI/browser/browser_view.dart / lib/helper/product_name_helper.dart) so
// the admin can preview what the app would do for a domain that has no rule
// override yet. This is a best-effort JS port for admin UX only — the app's
// Dart implementation is the source of truth.
const NON_PRODUCT_PATHS = ['/cart', '/checkout', '/wishlist', '/account', '/signin', '/login', '/orders']
const SHORT_LINK_HOSTS = ['a.co', 'tb.cn', 'e.tb.cn', 'qr.1688.com', 'share.google', 'a.aliexpress.com', 's.click.aliexpress.com']
const LOCALE_PREFIXES = ['www', 'm', 'tr', 'en', 'us', 'uk', 'ar']

function genericIsProductPage(host, path, segments) {
  if (NON_PRODUCT_PATHS.some(p => path.startsWith(p))) return false

  if (host.includes('amazon')) return path.includes('/dp/') || path.includes('/gp/product/')
  if (host.includes('ebay')) return path.includes('/itm/')
  if (host.includes('aliexpress')) return path.includes('/item/')
  if (host.includes('noon')) return /\/p\//.test(path) && !path.includes('/c/')
  if (host.includes('hm.com')) return path.includes('/productpage.')
  if (host.includes('nike.com')) return segments.includes('t')
  if (host.includes('puma.com')) return segments.includes('pd')

  if (segments.includes('product') || segments.includes('products') || segments.includes('urun')) return true
  if (segments.includes('p')) return true
  if (/-p-[a-z0-9]/i.test(path)) return true

  if (segments.length) {
    const last = segments[segments.length - 1]
    if (/\.(html?|php|aspx?|jsp)$/i.test(last) && /\d/.test(last)) return true
    const core = last.replace(/\.(html?|php|aspx?|jsp)$/i, '')
    if (/\d{6,}$/.test(core)) return true
  }

  return false
}

function genericExtractName(rawUrl, hostname, path, segments) {
  let host = hostname.toLowerCase()
  const labels = host.split('.')
  if (labels.length > 2 && LOCALE_PREFIXES.includes(labels[0])) {
    host = labels.slice(1).join('.')
  }
  if (SHORT_LINK_HOSTS.includes(host)) return null
  if (host.startsWith('login.') || host.includes('.login.') || path.includes('/login/') || path.includes('/signin/')) return null

  let candidate = null
  for (const seg of segments) {
    const core = seg.replace(/\.(html?|php|aspx?|jsp)$/i, '')
    if (/^\d+$/.test(core)) continue
    if (core.length < 8) continue
    if (!candidate || core.length > candidate.length) candidate = core
  }
  if (!candidate) return null

  const idPatterns = [/(?:[-_][a-zA-Z]{1,2})?[-_]\d{6,}/, /-[A-Za-z]{1,3}\d{6,}$/]
  let cutAt = null
  for (const re of idPatterns) {
    const m = re.exec(candidate)
    if (m && (cutAt === null || m.index < cutAt)) cutAt = m.index
  }
  let cut = cutAt !== null ? candidate.slice(0, cutAt) : candidate
  cut = cut.replace(/[-_/.]+$/, '')
  const name = cut.replace(/[-_+.]+/g, ' ').replace(/\s+/g, ' ').trim()
  return name || null
}

export default {
  props: ['addEditModal', 'form', 'isUpdate'],

  data() {
    return {
      loading: false,
      resolving: false,
      resolvedUrl: null,
      resolveError: null,
      testUrl: '',
      advancedOpen: false,
      pageMatchTypes: [
        { text: this.$t('linkRules.containsPathSubstringOption'), value: 'contains' },
        { text: this.$t('linkRules.regexOption'), value: 'regex' }
      ],
      nameExtractTypes: [
        { text: this.$t('linkRules.regexCaptureGroupOption'), value: 'regex' },
        { text: this.$t('linkRules.queryParameterOption'), value: 'query_param' }
      ]
    }
  },

  computed: {
    preview() {
      const effectiveUrl = this.resolvedUrl || this.testUrl
      if (!effectiveUrl) return null

      let url
      try {
        url = new URL(effectiveUrl.match(/^https?:\/\//i) ? effectiveUrl : `https://${effectiveUrl}`)
      } catch (e) {
        return null
      }

      let path
      try {
        path = decodeURIComponent(url.pathname).toLowerCase()
      } catch (e) {
        path = url.pathname.toLowerCase()
      }
      const segments = path.split('/').filter(Boolean)
      const host = url.hostname.toLowerCase()

      // Page detection: rule pattern (+ exclude override) if set, else generic.
      let isProductPage
      let detectionSource
      if (this.form.product_page_match_type === 'contains' && this.form.product_page_pattern) {
        isProductPage = path.includes(this.form.product_page_pattern.toLowerCase())
        detectionSource = 'this rule (contains)'
      } else if (this.form.product_page_match_type === 'regex' && this.form.product_page_pattern) {
        try {
          isProductPage = new RegExp(this.form.product_page_pattern, 'i').test(path)
          detectionSource = 'this rule (regex)'
        } catch (e) {
          isProductPage = false
          detectionSource = 'this rule — invalid regex'
        }
      } else {
        isProductPage = genericIsProductPage(host, path, segments)
        detectionSource = 'generic fallback'
      }
      if (isProductPage && this.form.exclude_path_prefixes) {
        const excluded = this.form.exclude_path_prefixes.split(',').map(p => p.trim().toLowerCase()).filter(Boolean)
        if (excluded.some(p => path.startsWith(p))) {
          isProductPage = false
          detectionSource += ', excluded by this rule\'s exclude list'
        }
      }

      // Name extraction: rule pattern if set and non-empty, else generic.
      let name = null
      let nameSource = null
      if (this.form.name_extract_match_type === 'regex' && this.form.name_extract_pattern) {
        try {
          const m = new RegExp(this.form.name_extract_pattern, 'i').exec(effectiveUrl)
          name = m && m[1] ? m[1].trim() : null
          nameSource = name ? 'this rule (regex)' : null
        } catch (e) {
          name = null
        }
      } else if (this.form.name_extract_match_type === 'query_param' && this.form.name_extract_pattern) {
        name = url.searchParams.get(this.form.name_extract_pattern)
        nameSource = name ? 'this rule (query param)' : null
      }
      if (name && this.form.name_cleanup_regex) {
        try {
          name = name.replace(new RegExp(this.form.name_cleanup_regex, 'i'), '').trim()
        } catch (e) {
          // ignore invalid cleanup regex in the preview
        }
      }
      if (!name) {
        name = genericExtractName(effectiveUrl, host, path, segments)
        nameSource = this.form.name_extract_match_type
          ? 'generic fallback (rule pattern produced nothing)'
          : 'generic fallback'
      }

      return { isProductPage, detectionSource, name, nameSource }
    }
  },

  watch: {
    testUrl(newVal) {
      this.resolvedUrl = null
      this.resolveError = null
      if (!newVal || this.form.domain) return
      try {
        const url = new URL(newVal.match(/^https?:\/\//i) ? newVal : `https://${newVal}`)
        this.form.domain = url.hostname.replace(/^www\./, '')
      } catch (e) {
        // not a parseable URL yet — leave domain untouched
      }
    }
  },

  methods: {
    closeDialog() {
      this.$validator.reset()
      this.testUrl = ''
      this.resolvedUrl = null
      this.resolveError = null
      this.advancedOpen = false
      this.$emit('close')
    },

    resolveShortLink() {
      if (!this.testUrl) return
      let token = localStorage.getItem('access_token')
      this.resolving = true
      this.resolveError = null

      Axios.post('api/link-rule/resolve-redirect', { url: this.testUrl }, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then((res) => {
          this.resolving = false
          this.resolvedUrl = res.data.resolved_url
        })
        .catch((error) => {
          this.resolving = false
          this.resolveError = error.response && error.response.data ? error.response.data.message : this.$t('linkRules.couldNotResolveUrl')
        })
    },

    submitForm() {
      let token = localStorage.getItem('access_token')

      this.$validator.validateAll().then((res) => {
        if (res) {
          this.loading = true

          if (this.isUpdate) {
            Axios.put(`api/link-rule/${this.form.link_rule_id}`, this.form, {
              headers: { Authorization: 'Bearer ' + token }
            })
              .then(() => {
                this.loading = false
                swal({
                  title: this.$t('common.updatedSuccessfully'),
                  text: '',
                  icon: 'success',
                  timer: 1500,
                  buttons: false
                })
                this.$emit('resetTable')
                this.closeDialog()
              })
              .catch((error) => {
                this.loading = false
                swal(this.$t('common.error'), error.response.data.message, 'error')
              })
          } else {
            Axios.post('api/link-rule', this.form, {
              headers: { Authorization: 'Bearer ' + token }
            })
              .then(() => {
                this.loading = false
                swal({
                  title: this.$t('common.addedSuccessfully'),
                  text: '',
                  icon: 'success',
                  timer: 1500,
                  buttons: false
                })
                this.$emit('resetTable')
                this.closeDialog()
              })
              .catch((error) => {
                this.loading = false
                swal(this.$t('common.error'), error.response.data.message, 'error')
              })
          }
        }
      })
    }
  }
}
</script>

<style>
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden !important;
  overflow-y: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/linkRules/addEdit.vue